import { useState, useRef, useEffect } from "react";
import HeaderSection from '../../components/HeaderSection/HeaderSection';
import BottomSection from '../../components/BottomSection/BottomSection';
import { useTheme } from '../../context/ThemeContext';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { faCompress } from "@fortawesome/free-solid-svg-icons";


export default function ImageCompressor() {
    const [file, setFile] = useState(null);
  const { darkMode } = useTheme();
    const [quality, setQuality] = useState(80);
    const [scale, setScale] = useState(100);
    const [isCompressing, setIsCompressing] = useState(false);
    const [originalImage, setOriginalImage] = useState(null);
    const [compressedImage, setCompressedImage] = useState(null);
    const [isDragActive, setIsDragActive] = useState(false);
    const [showComparison, setShowComparison] = useState(false);

    const fileInputRef = useRef(null);
    const uploadAreaRef = useRef(null);
    const beforeAfterRef = useRef(null);

    const formatFileSize = (bytes) => {
        if (bytes === 0) return "0 Bytes";
        const k = 1024;
        const sizes = ["Bytes", "KB", "MB", "GB"];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
    };

    const calculateReduction = (original, compressed) => {
        return Math.round(((original - compressed) / original) * 100);
    };

    const handleFileSelect = (e) => {
        const selectedFile =
            e.target.files?.[0] || (e.dataTransfer && e.dataTransfer.files[0]);
        if (!selectedFile) return;

        setFile(selectedFile);
        setCompressedImage(null);
        setShowComparison(false);

        const reader = new FileReader();
        reader.onload = (event) => {
            setOriginalImage({
                src: event.target.result,
                name: selectedFile.name,
                size: selectedFile.size,
            });
        };
        reader.readAsDataURL(selectedFile);
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();

        if (e.type === "dragover") {
            setIsDragActive(true);
        } else if (e.type === "dragleave") {
            setIsDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragActive(false);

        if (e.dataTransfer.files && e.dataTransfer.files.length) {
            fileInputRef.current.files = e.dataTransfer.files;
            handleFileSelect(e);
        }
    };

    const compressImage = () => {
        if (!file || !originalImage) return;

        setIsCompressing(true);
        setShowComparison(true);

        setTimeout(() => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement("canvas");
                const ctx = canvas.getContext("2d");

                const width = img.width * (scale / 100);
                const height = img.height * (scale / 100);

                canvas.width = width;
                canvas.height = height;

                ctx.drawImage(img, 0, 0, width, height);

                const compressedDataUrl = canvas.toDataURL(
                    "image/jpeg",
                    quality / 100
                );

                const originalSize = file.size;
                const compressedSize = Math.round(
                    (compressedDataUrl.length * 3) / 4
                );

                setCompressedImage({
                    src: compressedDataUrl,
                    quality: quality,
                    width: Math.round(width),
                    height: Math.round(height),
                    size: compressedSize,
                    reduction: calculateReduction(originalSize, compressedSize),
                });

                setIsCompressing(false);
            };

            img.src = originalImage.src;
        }, 300);
    };

    useEffect(() => {
        if (
            showComparison &&
            beforeAfterRef.current &&
            originalImage &&
            compressedImage
        ) {
            const container = beforeAfterRef.current;
            container.innerHTML = "";

            const beforeImg = document.createElement("img");
            beforeImg.src = originalImage.src;
            beforeImg.alt = "Original image before compression";
            beforeImg.className =
                "absolute top-0 left-0 w-full h-full object-contain";

            const afterImg = document.createElement("img");
            afterImg.src = compressedImage.src;
            afterImg.alt = "Compressed image result";
            afterImg.className =
                "absolute top-0 left-0 w-full h-full object-contain";

            const slider = document.createElement("div");
            slider.className =
                "absolute top-0 left-1/2 w-1 bg-white dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 h-full cursor-col-resize";
            slider.style.transform = "translateX(-50%)";

            const sliderButton = document.createElement("div");
            sliderButton.className =
                "absolute -left-3 top-1/2 w-6 h-6 rounded-full bg-white dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 shadow-md dark:shadow-gray-900/20 dark:shadow-gray-900/20 dark:shadow-gray-900/20 dark:shadow-gray-900/20 dark:shadow-gray-900/20 dark:shadow-gray-900/20 flex items-center justify-center";
            sliderButton.style.transform = "translateY(-50%)";

            const arrowLeft = document.createElement("div");
            arrowLeft.className =
                "w-2 h-2 border-l-2 border-b-2 border-gray-600 transform rotate-45 -translate-x-1/2";

            const arrowRight = document.createElement("div");
            arrowRight.className =
                "w-2 h-2 border-r-2 border-b-2 border-gray-600 transform -rotate-45 translate-x-1/2";

            sliderButton.appendChild(arrowLeft);
            sliderButton.appendChild(arrowRight);
            slider.appendChild(sliderButton);

            const afterDiv = document.createElement("div");
            afterDiv.className =
                "absolute top-0 left-0 w-1/2 h-full overflow-hidden";
            afterDiv.appendChild(afterImg);

            container.appendChild(beforeImg);
            container.appendChild(afterDiv);
            container.appendChild(slider);

            let isDragging = false;

            const moveSlider = (e) => {
                if (!isDragging) return;

                const containerRect = container.getBoundingClientRect();
                let x = e.clientX - containerRect.left;
                x = Math.max(0, Math.min(x, containerRect.width));

                const percent = (x / containerRect.width) * 100;
                afterDiv.style.width = `${percent}%`;
                slider.style.left = `${percent}%`;
            };

            slider.addEventListener("mousedown", () => {
                isDragging = true;
            });

            window.addEventListener("mousemove", moveSlider);
            window.addEventListener("mouseup", () => {
                isDragging = false;
            });

            return () => {
                window.removeEventListener("mousemove", moveSlider);
                window.removeEventListener("mouseup", () => {
                    isDragging = false;
                });
            };
        }
    }, [showComparison, originalImage, compressedImage]);

    return (
        <>
            <HeaderSection toolId="imageCompressor" />
            <div className=" max-w-4xl mx-auto p-4  lg:max-w-full lg:px-4">
          
        <main className="bg-white rounded-lg dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 rounded-xl shadow-sm dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 border border-gray-200 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 overflow-hidden">
            <article className="">
            <div className="bg-gradient-to-r from-green-500 to-green-600 p-6 text-white">
                        <h1 className="text-2xl font-bold mb-2 flex items-center">
                            <FontAwesomeIcon
                                icon={faCompress}
                                className="mr-3"
                            />
                            Image Compressor Tool
                        </h1>
                        <p className="opacity-90">
                            Compress images to reduce file size while maintaining quality
                        </p>
                    </div>

                <section className="bg-white dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 rounded-xl ">
                    <section className="p-6 border-b border-gray-200">
                        <figure
                            ref={uploadAreaRef}
                            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                                isDragActive
                                    ? "border-green-200 bg-green-50"
                                    : "border-gray-300 hover:border-gray-400"
                            }`}
                            onDragOver={handleDrag}
                            onDragLeave={handleDrag}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current.click()}
                        >
                            <input
                                ref={fileInputRef}
                                type="file"
                                id="imageInput"
                                className="hidden"
                                accept="image/*"
                                onChange={handleFileSelect}
                                aria-label="Select image to compress"
                            />
                            <figcaption className="flex flex-col items-center justify-center space-y-3">
                                <div className="p-4 bg-green-100 rounded-full">
                                    <svg
                                        className="w-8 h-8 text-green-500"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        aria-hidden="true"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                        />
                                    </svg>
                                </div>
                                <h2 className="text-gray-700 dark:text-white dark:text-white dark:text-white dark:text-white dark:text-gray-200 dark:text-gray-200 font-medium">
                                    Drag & drop your image here
                                </h2>
                                <p className="text-sm text-gray-500">
                                    or click to browse files (JPEG, PNG)
                                </p>
                                {file && (
                                    <p className="text-sm text-gray-600 dark:text-gray-100 dark:text-gray-100 dark:text-gray-100 dark:text-gray-100 dark:text-gray-300 dark:text-gray-300 mt-2 px-3 py-1 bg-gray-100 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 rounded-full">
                                        {file.name} •{" "}
                                        {formatFileSize(file.size)}
                                    </p>
                                )}
                            </figcaption>
                        </figure>
                    </section>

                    <section className="p-6 bg-gray-50">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 p-6">
                            <figure>
                                <label
                                    htmlFor="qualitySlider"
                                    className="block text-sm font-medium text-gray-700 dark:text-white dark:text-white dark:text-white dark:text-white dark:text-gray-200 dark:text-gray-200 mb-1"
                                >
                                    Quality:{" "}
                                    <span className="font-semibold text-green-500">
                                        {quality}%
                                    </span>
                                </label>
                                <input
                                    type="range"
                                    id="qualitySlider"
                                    min="10"
                                    max="100"
                                    value={quality}
                                    onChange={(e) => setQuality(e.target.value)}
                                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-500 focus:ring-2 focus:ring-green-200"
                                    aria-label="Image quality slider"
                                />
                                <figcaption className="flex justify-between text-xs text-gray-500 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-400 dark:text-gray-400 mt-1">
                                    <span>Low</span>
                                    <span>High</span>
                                </figcaption>
                            </figure>

                            <figure>
                                <label
                                    htmlFor="sizeSlider"
                                    className="block text-sm font-medium text-gray-700 dark:text-white dark:text-white dark:text-white dark:text-white dark:text-gray-200 dark:text-gray-200 mb-1"
                                >
                                    Size:{" "}
                                    <span className="font-semibold text-green-500">
                                        {scale}%
                                    </span>
                                </label>
                                <input
                                    type="range"
                                    id="sizeSlider"
                                    min="10"
                                    max="100"
                                    value={scale}
                                    onChange={(e) => setScale(e.target.value)}
                                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-green-500 focus:ring-2 focus:ring-green-200"
                                    aria-label="Image size slider"
                                />
                                <figcaption className="flex justify-between text-xs text-gray-500 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-400 dark:text-gray-400 mt-1">
                                    <span>Small</span>
                                    <span>Original</span>
                                </figcaption>
                            </figure>
                        </div>

                        <button
                            onClick={compressImage}
                            disabled={!file || isCompressing}
                            className={`w-full mt-6 py-3 px-4 rounded-lg font-medium text-white transition-colors focus:ring-2 focus:ring-green-200 focus:outline-none ${
                                !file || isCompressing
                                    ? "bg-gray-400 cursor-not-allowed"
                                    : "bg-green-500 hover:bg-green-600 shadow-md"
                            }`}
                            aria-label="Compress image button"
                        >
                            {isCompressing ? (
                                <span className="flex items-center justify-center">
                                    <svg
                                        className="animate-spin -ml-1 mr-2 h-5 w-5 text-white"
                                        xmlns="http://www.w3.org/2000/svg"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        aria-hidden="true"
                                    >
                                        <circle
                                            className="opacity-25"
                                            cx="12"
                                            cy="12"
                                            r="10"
                                            stroke="currentColor"
                                            strokeWidth="4"
                                        ></circle>
                                        <path
                                            className="opacity-75"
                                            fill="currentColor"
                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                        ></path>
                                    </svg>
                                    Compressing...
                                </span>
                            ) : (
                                <span className="flex items-center justify-center">
                                    <svg
                                        className="w-5 h-5 mr-2"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        aria-hidden="true"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth="2"
                                            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                        />
                                    </svg>
                                    Compress Image
                                </span>
                            )}
                        </button>
                    </section>

                    {originalImage && (
                        <section className="p-6 bg-white dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 rounded-xl">
                            <header className="text-2xl font-bold text-gray-800 dark:text-white dark:text-white dark:text-white dark:text-white dark:text-gray-100 dark:text-gray-100 mb-6 flex items-center">
                                <svg
                                    className="w-6 h-6 mr-2 text-green-500"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                    aria-hidden="true"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth="2"
                                        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                                    />
                                </svg>
                                Results
                            </header>

                            {showComparison && compressedImage ? (
                                <div className="space-y-8">
                                    <article className="border border-gray-200 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 hover:shadow-md dark:shadow-gray-900/20 dark:shadow-gray-900/20 dark:shadow-gray-900/20 dark:shadow-gray-900/20 dark:shadow-gray-900/20 dark:shadow-gray-900/20 transition-shadow duration-300">
                                        <header className="bg-gray-50 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 px-5 py-4 border-b flex justify-between items-center">
                                            <h3 className="font-semibold text-gray-700 dark:text-white dark:text-white dark:text-white dark:text-white dark:text-gray-200 dark:text-gray-200 flex items-center">
                                                <svg
                                                    className="w-5 h-5 mr-2 text-green-500"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                    aria-hidden="true"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth="2"
                                                        d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"
                                                    />
                                                </svg>
                                                Image Comparison
                                            </h3>
                                            <div className="text-sm bg-white dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 px-3 py-1 rounded-full shadow-inner border border-gray-200">
                                                <span className="font-medium text-gray-600">
                                                    Original
                                                </span>
                                                <span className="mx-1 text-gray-400">
                                                    |
                                                </span>
                                                <span className="font-medium text-green-500">
                                                    Compressed
                                                </span>
                                            </div>
                                        </header>

                                        <figure className="flex justify-center items-center w-full h-96 bg-gray-100 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 overflow-hidden">
                                            <div
                                                ref={beforeAfterRef}
                                                className="w-[80%] h-[80%] relative"
                                                aria-label="Image comparison slider"
                                            ></div>
                                        </figure>

                                        <footer className="bg-gray-50 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 px-5 py-3 text-xs text-gray-500 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-400 dark:text-gray-400 border-t text-center">
                                            Drag the slider to compare before and
                                            after images
                                        </footer>
                                    </article>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <article className="border border-gray-200 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 hover:shadow-md dark:shadow-gray-900/20 dark:shadow-gray-900/20 dark:shadow-gray-900/20 dark:shadow-gray-900/20 dark:shadow-gray-900/20 dark:shadow-gray-900/20 transition-shadow duration-300">
                                            <header className="bg-gray-50 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 px-5 py-4 border-b">
                                                <h3 className="font-semibold text-gray-700 dark:text-white dark:text-white dark:text-white dark:text-white dark:text-gray-200 dark:text-gray-200 flex items-center">
                                                    <svg
                                                        className="w-5 h-5 mr-2 text-gray-500"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                        aria-hidden="true"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth="2"
                                                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                                        />
                                                    </svg>
                                                    Original Image
                                                </h3>
                                            </header>
                                            <div className="p-5">
                                                <div className="flex flex-col md:flex-row gap-5">
                                                    <figure className="flex-shrink-0 w-full md:w-2/5 h-48 bg-white dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 shadow-inner hover:shadow-sm dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 transition-shadow">
                                                        <img
                                                            src={
                                                                originalImage.src
                                                            }
                                                            alt="Original"
                                                            className="w-full h-full object-contain p-2"
                                                            loading="lazy"
                                                        />
                                                    </figure>
                                                    <div className="text-sm text-gray-600 dark:text-gray-100 dark:text-gray-100 dark:text-gray-100 dark:text-gray-100 dark:text-gray-300 dark:text-gray-300 space-y-3">
                                                        <section className="bg-gray-50 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 p-3 rounded-lg hover:bg-green-50 transition-colors">
                                                            <h4 className="font-medium text-gray-500 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-400 dark:text-gray-400 mb-1">
                                                                File Name
                                                            </h4>
                                                            <p className="truncate font-semibold">
                                                                {
                                                                    originalImage.name
                                                                }
                                                            </p>
                                                        </section>
                                                        <section className="bg-gray-50 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 p-3 rounded-lg hover:bg-green-50 transition-colors">
                                                            <h4 className="font-medium text-gray-500 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-400 dark:text-gray-400 mb-1">
                                                                Dimensions
                                                            </h4>
                                                            <p className="font-semibold">
                                                                Original
                                                                resolution
                                                            </p>
                                                        </section>
                                                        <section className="bg-gray-50 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 p-3 rounded-lg hover:bg-green-50 transition-colors">
                                                            <h4 className="font-medium text-gray-500 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-400 dark:text-gray-400 mb-1">
                                                                File Size
                                                            </h4>
                                                            <p className="font-semibold">
                                                                {formatFileSize(
                                                                    originalImage.size
                                                                )}
                                                            </p>
                                                        </section>
                                                    </div>
                                                </div>
                                            </div>
                                        </article>

                                        <article className="border border-gray-200 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 hover:shadow-md dark:shadow-gray-900/20 dark:shadow-gray-900/20 dark:shadow-gray-900/20 dark:shadow-gray-900/20 dark:shadow-gray-900/20 dark:shadow-gray-900/20 transition-shadow duration-300">
                                            <header className="bg-gray-50 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 px-5 py-4 border-b">
                                                <h3 className="font-semibold text-gray-700 dark:text-white dark:text-white dark:text-white dark:text-white dark:text-gray-200 dark:text-gray-200 flex items-center">
                                                    <svg
                                                        className="w-5 h-5 mr-2 text-green-500"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                        aria-hidden="true"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth="2"
                                                            d="M12 15V3m0 0l-4 4m4-4l4 4M3 15a9 9 0 1018 0 9 9 0 00-18 0z"
                                                        />
                                                    </svg>
                                                    Compressed Image
                                                </h3>
                                            </header>
                                            <div className="p-5">
                                                <div className="flex flex-col md:flex-row gap-5">
                                                    <figure className="flex-shrink-0 w-full md:w-2/5 h-48 bg-white dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 shadow-inner hover:shadow-sm dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 transition-shadow">
                                                        <img
                                                            src={
                                                                compressedImage.src
                                                            }
                                                            alt="Compressed"
                                                            className="w-full h-full object-contain p-2"
                                                            loading="lazy"
                                                        />
                                                    </figure>
                                                    <div className="text-sm text-gray-600 dark:text-gray-100 dark:text-gray-100 dark:text-gray-100 dark:text-gray-100 dark:text-gray-300 dark:text-gray-300 space-y-3">
                                                        <section className="bg-gray-50 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 p-3 rounded-lg hover:bg-green-50 transition-colors">
                                                            <h4 className="font-medium text-gray-500 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-400 dark:text-gray-400 mb-1">
                                                                Quality
                                                            </h4>
                                                            <p className="font-semibold">
                                                                {
                                                                    compressedImage.quality
                                                                }
                                                                %
                                                            </p>
                                                        </section>
                                                        <section className="bg-gray-50 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 p-3 rounded-lg hover:bg-green-50 transition-colors">
                                                            <h4 className="font-medium text-gray-500 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-400 dark:text-gray-400 mb-1">
                                                                Dimensions
                                                            </h4>
                                                            <p className="font-semibold">
                                                                {
                                                                    compressedImage.width
                                                                }{" "}
                                                                ×{" "}
                                                                {
                                                                    compressedImage.height
                                                                }{" "}
                                                                px
                                                            </p>
                                                        </section>
                                                        <section className="bg-gray-50 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 p-3 rounded-lg hover:bg-green-50 transition-colors">
                                                            <h4 className="font-medium text-gray-500 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-400 dark:text-gray-400 mb-1">
                                                                File Size
                                                            </h4>
                                                            <p className="font-semibold">
                                                                {formatFileSize(
                                                                    compressedImage.size
                                                                )}{" "}
                                                                •{" "}
                                                                <span className="text-green-500">
                                                                    {
                                                                        compressedImage.reduction
                                                                    }
                                                                    % smaller
                                                                </span>
                                                            </p>
                                                        </section>
                                                    </div>
                                                </div>
                                            </div>
                                        </article>
                                    </div>

                                    <footer className="text-center pt-4">
                                        <a
                                            href={compressedImage.src}
                                            download={`compressed_${file.name}`}
                                            className="inline-flex items-center px-8 py-4 bg-green-500 text-white rounded-xl hover:bg-green-600 shadow-lg dark:shadow-gray-900/30 dark:shadow-gray-900/30 dark:shadow-gray-900/30 dark:shadow-gray-900/30 dark:shadow-gray-900/30 dark:shadow-gray-900/30 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-0.5 focus:ring-2 focus:ring-green-200 focus:outline-none"
                                            aria-label="Download compressed image"
                                        >
                                            <svg
                                                className="w-6 h-6 mr-3"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                                aria-hidden="true"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth="2"
                                                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                                                />
                                            </svg>
                                            <span className="text-lg font-semibold">
                                                Download Compressed Image
                                            </span>
                                        </a>
                                    </footer>
                                </div>
                            ) : (
                                <article className="border border-gray-200 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 rounded-xl overflow-hidden shadow-sm dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 dark:shadow-gray-900/10 hover:shadow-md dark:shadow-gray-900/20 dark:shadow-gray-900/20 dark:shadow-gray-900/20 dark:shadow-gray-900/20 dark:shadow-gray-900/20 dark:shadow-gray-900/20 transition-shadow duration-300">
                                    <header className="bg-gray-50 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 px-5 py-4 border-b">
                                        <h3 className="font-semibold text-gray-700 dark:text-white dark:text-white dark:text-white dark:text-white dark:text-gray-200 dark:text-gray-200 flex items-center">
                                            <svg
                                                className="w-5 h-5 mr-2 text-gray-500"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                                aria-hidden="true"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth="2"
                                                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                                />
                                            </svg>
                                            Original Image Preview
                                        </h3>
                                    </header>
                                    <div className="p-6">
                                        <div className="flex flex-col md:flex-row gap-8">
                                            <figure className="flex-shrink-0 w-full md:w-1/2 h-72 bg-white dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 dark:bg-gray-800 rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 dark:border-gray-700 shadow-inner hover:shadow transition-all">
                                                <img
                                                    src={originalImage.src}
                                                    alt="Original"
                                                    className="w-full h-full object-contain p-4"
                                                    loading="lazy"
                                                />
                                            </figure>
                                            <div className="flex flex-col justify-between">
                                                <div className="space-y-4">
                                                    <section className="bg-gray-50 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 p-4 rounded-lg hover:bg-green-50 transition-colors">
                                                        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-400 dark:text-gray-400 mb-1">
                                                            File Name
                                                        </h4>
                                                        <p className="font-semibold text-gray-700">
                                                            {originalImage.name}
                                                        </p>
                                                    </section>
                                                    <section className="bg-gray-50 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 p-4 rounded-lg hover:bg-green-50 transition-colors">
                                                        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-400 dark:text-gray-400 mb-1">
                                                            File Size
                                                        </h4>
                                                        <p className="font-semibold text-gray-700">
                                                            {formatFileSize(
                                                                originalImage.size
                                                            )}
                                                        </p>
                                                    </section>
                                                    <section className="bg-gray-50 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 dark:bg-gray-800 dark:bg-gray-900 p-4 rounded-lg hover:bg-green-50 transition-colors">
                                                        <h4 className="text-sm font-medium text-gray-500 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-200 dark:text-gray-400 dark:text-gray-400 mb-1">
                                                            Compression Settings
                                                        </h4>
                                                        <div className="flex items-center space-x-4">
                                                            <div>
                                                                <p className="text-xs text-gray-500">
                                                                    Quality
                                                                </p>
                                                                <p className="font-semibold text-green-500">
                                                                    {quality}%
                                                                </p>
                                                            </div>
                                                            <div className="h-6 w-px bg-gray-300"></div>
                                                            <div>
                                                                <p className="text-xs text-gray-500">
                                                                    Size
                                                                </p>
                                                                <p className="font-semibold text-green-500">
                                                                    {scale}%
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </section>
                                                </div>
                                                <button
                                                    onClick={compressImage}
                                                    disabled={isCompressing}
                                                    className="mt-6 flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-xl hover:bg-green-600 shadow-md dark:shadow-gray-900/20 dark:shadow-gray-900/20 dark:shadow-gray-900/20 dark:shadow-gray-900/20 dark:shadow-gray-900/20 dark:shadow-gray-900/20 hover:shadow-lg dark:shadow-gray-900/30 dark:shadow-gray-900/30 dark:shadow-gray-900/30 dark:shadow-gray-900/30 dark:shadow-gray-900/30 dark:shadow-gray-900/30 transition-all duration-300 transform hover:-translate-y-0.5 focus:ring-2 focus:ring-green-200 focus:outline-none disabled:opacity-70 disabled:transform-none disabled:cursor-not-allowed"
                                                    aria-label="Compress image button"
                                                >
                                                    {isCompressing ? (
                                                        <>
                                                            <svg
                                                                className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                                                                xmlns="http://www.w3.org/2000/svg"
                                                                fill="none"
                                                                viewBox="0 0 24 24"
                                                                aria-hidden="true"
                                                            >
                                                                <circle
                                                                    className="opacity-25"
                                                                    cx="12"
                                                                    cy="12"
                                                                    r="10"
                                                                    stroke="currentColor"
                                                                    strokeWidth="4"
                                                                ></circle>
                                                                <path
                                                                    className="opacity-75"
                                                                    fill="currentColor"
                                                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                                ></path>
                                                            </svg>
                                                            Compressing...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <svg
                                                                className="w-5 h-5 mr-3"
                                                                fill="none"
                                                                stroke="currentColor"
                                                                viewBox="0 0 24 24"
                                                                aria-hidden="true"
                                                            >
                                                                <path
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                    strokeWidth="2"
                                                                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                                                />
                                                            </svg>
                                                            Compress Now
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </article>
                            )}
                        </section>
                    )}
                </section>

                <footer className="text-center mt-8 text-sm text-gray-500">
                    <p>
                        Drag the slider on the comparison image to see
                        before/after effects
                    </p>
                </footer>
            </article>
        </main>
        </div>
        <BottomSection toolId="imageCompressor" />
        </>
    );
}